import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ShareOptions } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import WishListSharingStatus, {
    WishListSharingStatusStyles,
} from "@insite/content-library/Components/WishListSharingStatus";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Star from "@insite/mobius/Icons/Star";
import StarX from "@insite/mobius/Icons/StarX";
import Tag from "@insite/mobius/Icons/Tag";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";
import React, { useContext } from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    allowEditingOfWishLists: getSettingsCollection(state).wishListSettings.allowEditingOfWishLists,
    isUpdatingCart: state.context.isUpdatingCart,
    listDetailsPageUrl: getPageLinkByPageType(state, "MyListsDetailsPage")?.url,
});

interface OwnProps {
    wishList: WishListModel;
    extendedStyles?: WishListCardStyles;
    addWishListToCart: HandleThunkActionCreator<typeof addWishListToCart>;
    deleteWishList?: () => void;
    leaveWishList?: () => void;
    toggleFavorite?: () => void;
    toggleHide?: () => void;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface WishListCardStyles {
    gridContainer?: GridContainerProps;
    wishListInfoGridItem?: GridItemProps;
    wishListInfoGridContainer?: GridContainerProps;
    basicInfoGridItem?: GridItemProps;
    basicInfoGridContainer?: GridContainerProps;
    detailLinkGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyProps;
    lastUpdatedGridItem?: GridItemProps;
    noItemsAddedGridItem?: GridItemProps;
    lastUpdatedText?: TypographyProps;
    extendedInfoGridItem?: GridItemProps;
    extendedInfoGridContainer?: GridContainerProps;
    extendedInfoGridContainerBottom?: GridContainerProps;
    noItemsAdded?: TypographyProps;
    productImagesGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    productImage?: LazyImageProps;
    sharingStatusGridItem?: GridItemProps;
    sharingStatus?: WishListSharingStatusStyles;
    actionGridItem?: GridItemProps;
    actionGridMediumHidden?: HiddenProps;
    actionOverflowIcon?: IconPresentationProps;
    actionOverflowMenu?: OverflowMenuPresentationProps;
    actionAddToCartClickable?: ClickablePresentationProps;
    actionHideClickable?: ClickablePresentationProps;
    actionDeleteClickable?: ClickablePresentationProps;
    actionLeaveClickable?: ClickablePresentationProps;
    actionAddToCartButton?: ButtonPresentationProps;
    actionHideLink?: LinkPresentationProps;
    actionDeleteButton?: ButtonPresentationProps;
    actionLeaveButton?: ButtonPresentationProps;
    actionFavoriteClickable?: ClickablePresentationProps;
    basicIconGridContainer?: GridContainerProps;
    detailIconGridItem?: GridItemProps;
    starIcon?: IconPresentationProps;
    starXIcon?: IconPresentationProps;
    tagTooltip?: TooltipPresentationProps;
    basicInfogrid?: GridItemProps;
    wishListNameContainer?: InjectableCss;
    wishListName?: LinkPresentationProps;
    wishListCount?: InjectableCss;
}

export const wishListCardStyles: WishListCardStyles = {
    gridContainer: {
        gap: 5,
        css: css`
            background: ${getColor("common.accent")} !important;
        `,
    },
    wishListInfoGridItem: {
        width: [11, 11, 11, 11, 11],
        align: "middle",
    },
    basicInfoGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0 !important;
        `,
    },
    basicInfoGridContainer: {
        gap: 0,
    },
    wishListInfoGridContainer: {
        css: css`
            /* div[class^="GridItemStyle"]{
            padding:0 !important;
        } */
        `,
    },
    detailLinkGridItem: {
        width: 12,
        css: css`
            padding-top: 11px !important;
            padding-left: 15px !important;
            align-items: center;
        `,
    },
    actionOverflowMenu: {
        css: css`
            button[data-test-selector^="popoverOverflowTrigger"] {
                background: transparent;
                border-color: transparent;
            }
        `,
    },
    descriptionGridItem: { width: 12 },
    descriptionText: { ellipsis: true },
    lastUpdatedGridItem: { width: [8, 8, 6, 6, 6] },
    noItemsAddedGridItem: {
        width: 12,
        css: css`
            margin-left: 15px !important;
            padding: 34px;
            justify-content: center;
        `,
    },
    lastUpdatedText: {
        css: css`
            font-size: 10px;
            display: contents;
        `,
    },
    noItemsAdded: {
        css: css`
            font-size: 14px;
        `,
    },
    basicInfogrid: {
        width: 12,
        css: css`
            /* margin-top:16px !important;  */
        `,
    },
    extendedInfoGridItem: {
        width: [12, 12, 12, 12, 12],
        align: "middle",
        css: css`
            padding: 0px !important;
        `,
    },
    productImageLink: {
        css: css`
            margin-right: 10px;
        `,
    },
    productImagesGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            flex-wrap: wrap;
            padding-left: 30px;
        `,
    },
    productImage: {
        css: css`
            /* max-width: 75px; */
            width: 80px;
            background: ${WhitecapColors.white};
            img {
                /* height: 100%; */
                height: 80px;
                margin: 0px auto;
                max-width: 80px;
                object-fit: contain;
                width: 100%;
            }
        `,
        errorTypographyProps: {
            size: 11,
            css: css`
                max-height: 65px;
            `,
        },
    },
    sharingStatusGridItem: {
        width: [4, 4, 6, 6, 6],
        css: css`
            justify-content: end;
            align-items: center;
            span[data-test-selector^="myLists_PrivateLabel"] {
                font-size: 12px;
                display: flex;
            }
        `,
    },
    actionGridItem: {
        width: [1, 1, 1, 1, 1],
        align: "top",
        css: css`
            justify-content: flex-end;
            align-items: flex-start;
            nav[class^="OverflowWrapper"] {
                width: 25px !important;
                margin-top: -6px;
            }
            button[data-test-selector^="popoverOverflowTrigger"] {
                background: transparent;
                border-color: transparent;
                display: flex;
                justify-content: center;
            }
        `,
    },
    actionGridMediumHidden: {
        css: css`
            width: 100%;
        `,
    },
    actionAddToCartButton: {
        color: "secondary",
        css: css`
            white-space: nowrap;
            width: 100%;
            padding: 0 10px;
        `, // keep text from wrapping
    },
    actionHideLink: {
        css: css`
            display: block;
            margin: 10px auto 0 auto;
        `,
    },
    actionDeleteButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    actionLeaveButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    sharingStatus: {
        privateTooltip: {
            cssOverrides: {
                tooltipContainer: css`
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css` left: -55px`,
                            css` left: -55px`,
                            css`
                                margin-left: -65px;
                                bottom: auto;
                                left: -52px;
                                top: 0;
                                z-index: 1;
                            `,
                            null,
                            null,
                        ])}
                `,
                tooltipArrow: css`
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            null,
                            null,
                            css`
                                top: 9%;
                                left: 100%;
                                -webkit-transform: rotate(-90deg);
                            `,
                            null,
                            null,
                        ])}
                `,
            },
        },
    },
    basicIconGridContainer: {
        gap: 10,
        css: css`
            width: 15px;
            flex-shrink: 0;
        `,
        offsetCss: css`
            flex-direction: column;
        `,
    },
    actionFavoriteClickable: {
        css: css`
            width: 15px;
        `,
    },
    starIcon: {
        src: Star,
        size: 15,
    },
    starXIcon: {
        src: StarX,
        size: 15,
    },
    extendedInfoGridContainerBottom: {
        css: css`
            margin-top: 34px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 16px;
                    `,
                    css`
                        margin-top: 16px;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    tagTooltip: {
        iconProps: {
            src: Tag,
            size: 15,
            css: css`
                margin: 4px 0;
            `,
        },
    },
    wishListNameContainer: {
        css: css`
            max-width: calc(100% - 30px);
        `,
    },
    wishListName: {
        css: css`
            max-width: 100%;
        `,
        typographyProps: {
            css: css`
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 100%;
                display: inline-block;
                padding-left: 0 !important;
                padding-top: 0px !important;

                font-size: 16px;
                font-weight: 500;
                margin-bottom: 16px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: 14px;
                            margin-bottom: 10px;
                        `,
                        css`
                            font-size: 14px;
                            margin-bottom: 10px;
                        `,
                    ])}
            `,
        },
    },
    wishListCount: {
        css: css`
            padding-left: 5px;
            flex-shrink: 0;
            padding-top: 0px !important;
            font-size: 16px;
            font-weight: 500;
            margin-bottom: 16px;
            color: ${getColor("text.link")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 14px;
                        margin-bottom: 10px;
                        color: ${getColor("text.main")};
                    `,
                    css`
                        font-size: 14px;
                        margin-bottom: 10px;
                        color: ${getColor("text.main")};
                    `,
                ])}
        `,
    },
};

const RecentWishListCard = ({
    language,
    allowEditingOfWishLists,
    isUpdatingCart,
    extendedStyles,
    wishList,
    addWishListToCart,
    deleteWishList,
    leaveWishList,
    toggleFavorite,
    toggleHide,
    listDetailsPageUrl,
}: Props) => {

    const toasterContext = useContext(ToasterContext);
    const isLoadingAnyList = useAppSelector(state => Object.keys(state.data.wishLists.isLoading).length > 0);

    const clickAddToCartHandler = (e: any) => {
        e.preventDefault();
        addWishListToCart({
            apiParameter: { wishListId: wishList.id },
            onSuccess: onAddAllToCartSuccess,
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    };

    const onAddAllToCartSuccess = (result: CartLineCollectionModel) => {
        toasterContext.addToast({
            body: result.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : translate("List Added to Cart"),
            messageType: "success",
        });
    };

    const updatedOnDisplay = getLocalizedDateTime({
        dateTime: new Date(wishList.updatedOn),
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    let lastUpdatedDisplay = `${translate("Updated")} ${updatedOnDisplay}`;
    if (wishList.updatedByDisplayName && !wishList.isAutogenerated) {
        lastUpdatedDisplay += ` ${translate("by")} ${wishList.updatedByDisplayName}`;
    }

    const canAddToCart = wishList.canAddToCart && !!wishList.wishListLinesCount && wishList.wishListLinesCount > 0;
    const addToCartLabel = translate("Add List to Cart");
    const toggleHideButtonText = wishList.isHidden ? translate("Unhide List") : translate("Hide List");
    const deleteLabel = translate("Delete");
    const leaveLabel = translate("Leave List");
    const [styles] = React.useState(() => mergeToNew(wishListCardStyles, extendedStyles));

    return (
        <GridContainer {...styles.gridContainer} data-test-selector="wishListCard">
            <GridItem {...styles.wishListInfoGridItem} data-test-selector={`${wishList.name}`}>
                <GridContainer {...styles.wishListInfoGridContainer}>
                    <GridItem {...styles.basicInfoGridItem}>
                        <GridContainer {...styles.basicInfoGridContainer}>
                            <GridItem {...styles.detailLinkGridItem}>
                                {/* <MyListsDetailsPageTypeLink
                                    title={`${wishList.name} (${wishList.wishListLinesCount})`}
                                    wishListId={wishList.id}
                                    testSelector="wishListCardName"
                                /> */}
                                <StyledWrapper {...styles.wishListNameContainer}>
                                    <Link href={`${listDetailsPageUrl}?id=${wishList.id}`} {...styles.wishListName}>
                                        {wishList.name}
                                    </Link>
                                </StyledWrapper>

                                <Typography {...styles.wishListCount}>{`(${wishList.wishListLinesCount})`}</Typography>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.extendedInfoGridItem}>
                        <GridContainer {...styles.extendedInfoGridContainer}>
                            <GridItem {...styles.productImagesGridItem} data-test-selector="productImagesGridItem">
                                {wishList.wishListLineCollection &&
                                    wishList.wishListLineCollection.length > 0 &&
                                    wishList.wishListLineCollection.slice(0, 3).map(line => (
                                        <Link
                                            key={line.id.toString()}
                                            {...styles.productImageLink}
                                            href={line.productUri}
                                        >
                                            <LazyImage
                                                {...styles.productImage}
                                                src={line.smallImagePath}
                                                altText={line.altText}
                                                title={line.altText}
                                            />
                                        </Link>
                                    ))}
                            </GridItem>
                            {wishList.wishListLinesCount === 0 && (
                                <GridItem {...styles.noItemsAddedGridItem}>
                                    <Typography as="p" {...styles.noItemsAdded}>
                                        {translate("No Items added.")}
                                    </Typography>
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem {...styles.actionGridItem}>
                <OverflowMenu position="end" {...styles.actionOverflowMenu}>
                    <Clickable
                        {...styles.actionAddToCartClickable}
                        disabled={!canAddToCart || isUpdatingCart}
                        onClick={clickAddToCartHandler}
                    >
                        {addToCartLabel}
                    </Clickable>
                    {!wishList.isAutogenerated && (
                        <Clickable {...styles.actionHideClickable} disabled={isLoadingAnyList} onClick={toggleHide}>
                            {toggleHideButtonText}
                        </Clickable>
                    )}
                    {allowEditingOfWishLists &&
                        !!deleteWishList &&
                        !wishList.isSharedList &&
                        !wishList.isAutogenerated && (
                            <Clickable {...styles.actionDeleteClickable} onClick={deleteWishList}>
                                {deleteLabel}
                            </Clickable>
                        )}
                    {allowEditingOfWishLists &&
                        !!leaveWishList &&
                        wishList.isSharedList &&
                        wishList.shareOption !== ShareOptions.AllCustomerUsers && (
                            <Clickable {...styles.actionLeaveClickable} onClick={leaveWishList}>
                                {leaveLabel}
                            </Clickable>
                        )}
                </OverflowMenu>
            </GridItem>
            <GridItem {...styles.basicInfogrid}>
                <GridContainer {...styles.extendedInfoGridContainerBottom}>
                    <GridItem {...styles.lastUpdatedGridItem}>
                        <Typography {...styles.lastUpdatedText} data-test-selector="wishListCardLastUpdated">
                            {lastUpdatedDisplay}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.sharingStatusGridItem}>
                        <WishListSharingStatus extendedStyles={styles.sharingStatus} wishList={wishList} />
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(RecentWishListCard);
